import React, { useState, useEffect } from 'react';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import {
  Modal,
  Button,
  TextField,
  Box,
  Typography,
  Snackbar,
  Alert,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
} from '@mui/material';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import axios from '../api/axios';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { useAuth } from '../contexts/authcontext';
import SimpleCallButton from '../components/SimpleCallButton';

const localizer = momentLocalizer(moment);

const DoctorDashboard = () => {
  const { user } = useAuth();
  const [availableSlots, setAvailableSlots] = useState([]);
  const [bookedSlots, setBookedSlots] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [startTime, setStartTime] = useState('09:00');
  const [endTime, setEndTime] = useState('17:00');
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [viewMode, setViewMode] = useState('calendar');
  const [loading, setLoading] = useState(false);
  const [appointmentDetailsModal, setAppointmentDetailsModal] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [currentDate, setCurrentDate] = useState(new Date());

  useEffect(() => {
    if (!user) return;

    fetchAvailableSlots();
    fetchBookedSlots();
  }, [user]);

  const fetchAvailableSlots = async () => {
    try {
      const response = await axios.get('/doctor/available-slots');

      if (Array.isArray(response.data)) {
        const slots = response.data.map(slot => ({
          id: slot.id,
          title: slot.display_status === 'expired' ? 'Expired' : 'Available',
          start: new Date(slot.start_time),
          end: new Date(slot.end_time),
          status: slot.display_status || slot.status
        }));

        setAvailableSlots(slots);
      } else {
        setAvailableSlots([]);
      }
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error('Session expired. Please login again.');
      } else if (error.response?.status === 403) {
        toast.error('This page is for therapist accounts only.');
      } else {
        toast.error('Failed to fetch available slots');
      }
      setAvailableSlots([]);
    }
  };

  const fetchBookedSlots = async () => {
    try {
      const response = await axios.get('/doctor/booked-slots');

      let slotsData = [];
      if (Array.isArray(response.data)) {
        slotsData = response.data;
      } else if (response.data && Array.isArray(response.data.slots)) {
        slotsData = response.data.slots;
      }

      if (slotsData.length > 0) {
        const bookings = slotsData.map(booking => {
          const startDate = new Date(`${booking.date} ${booking.time}`);
          const endDate = new Date(startDate.getTime() + 60 * 60 * 1000);

          return {
            // This is the bookings table row id. It is passed to
            // SimpleCallButton as bookingId so the call can be linked to the
            // booking and marked completed when it ends (audit #19).
            id: booking.id,
            title: `Booked - ${booking.client_name || 'Unknown'}`,
            start: startDate,
            end: endDate,
            status: 'booked',
            clientName: booking.client_name || 'Unknown',
            clientEmail: booking.client_email || 'Unknown',
            clientPhone: booking.client_phone || 'N/A',
            clientId: booking.client_id,
          };
        });

        setBookedSlots(bookings);
      } else {
        setBookedSlots([]);
      }
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error('Session expired. Please login again.');
      } else if (error.response?.status === 403) {
        toast.error('This page is for therapist accounts only.');
      } else {
        toast.error('Failed to fetch booked appointments');
      }
      setBookedSlots([]);
    }
  };

  const handleSelectSlot = (slotInfo) => {
    setSelectedDate(slotInfo.start);
    setIsEditMode(false);
    setOpenModal(true);
  };

  const handleSelectEvent = (event) => {
    if (event.status === 'booked') {
      setSelectedAppointment(event);
      setAppointmentDetailsModal(true);
    } else if (event.status === 'available') {
      setSelectedSlot(event);
      setSelectedDate(event.start);
      setStartTime(moment(event.start).format('HH:mm'));
      setEndTime(moment(event.end).format('HH:mm'));
      setIsEditMode(true);
      setOpenModal(true);
    }
  };

  const handleAddSlots = async () => {
    if (!selectedDate || !startTime || !endTime) {
      toast.error('Please fill in all fields');
      return;
    }

    if (startTime >= endTime) {
      toast.error('End time must be after start time');
      return;
    }

    setLoading(true);
    try {
      const dateStr = moment(selectedDate).format('YYYY-MM-DD');

      if (isEditMode && selectedSlot) {
        // Requires PUT /doctor/available-slots/:slot_id to be registered in
        // routes.js — updateAvailableSlot exists in dochomecontroller.js but
        // had no route, so editing a slot returned 404.
        await axios.put(`/doctor/available-slots/${selectedSlot.id}`, {
          start_time: `${dateStr} ${startTime}:00`,
          end_time: `${dateStr} ${endTime}:00`
        });
        toast.success('Slot updated successfully');
      } else {
        await axios.post('/doctor/add-available-slots', {
          date: dateStr,
          start_time: startTime,
          end_time: endTime
        });
        toast.success('Slot added successfully');
      }

      setOpenModal(false);
      setSelectedDate(null);
      setStartTime('09:00');
      setEndTime('17:00');
      setSelectedSlot(null);
      setIsEditMode(false);
      fetchAvailableSlots();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to save slot');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteSlot = async (slotId) => {
    if (!window.confirm('Are you sure you want to delete this slot?')) return;

    try {
      await axios.delete(`/doctor/available-slots/${slotId}`);
      toast.success('Slot deleted successfully');
      fetchAvailableSlots();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to delete slot');
    }
  };

  const eventStyleGetter = (event) => ({
    style: {
      backgroundColor:
        event.status === 'available' ? '#4CAF50' :
        event.status === 'expired' ? '#9E9E9E' : '#FF5722',
      color: 'white',
      border: 'none',
      display: 'block',
      padding: '2px 5px',
      borderRadius: '3px',
      cursor: 'pointer',
    },
  });

  const handlePreviousMonth = () => {
    setCurrentDate(moment(currentDate).subtract(1, 'month').toDate());
  };

  const handleNextMonth = () => {
    setCurrentDate(moment(currentDate).add(1, 'month').toDate());
  };

  const SlotListView = () => (
    <List>
      {availableSlots.map((slot) => (
        <ListItem key={slot.id}>
          <ListItemText
            primary={moment(slot.start).format('MMMM D, YYYY')}
            secondary={
              <>
                <Typography variant="body2" component="span" display="block">
                  {moment(slot.start).format('h:mm A')} - {moment(slot.end).format('h:mm A')}
                </Typography>
                <Typography variant="body2" component="span" color="textSecondary">
                  Status: {slot.status === 'expired' ? 'Expired' : 'Available'}
                </Typography>
              </>
            }
          />
          <ListItemSecondaryAction>
            {slot.status !== 'expired' && (
              <Button onClick={() => handleDeleteSlot(slot.id)} color="error">
                Delete
              </Button>
            )}
          </ListItemSecondaryAction>
        </ListItem>
      ))}

      {bookedSlots.map((slot) => (
        <ListItem key={`booked-${slot.id}`}>
          <ListItemText
            primary={moment(slot.start).format('MMMM D, YYYY')}
            secondary={
              <>
                <Typography variant="body2" component="span" display="block">
                  {moment(slot.start).format('h:mm A')} - {moment(slot.end).format('h:mm A')}
                </Typography>
                <Typography variant="body2" component="span" color="textSecondary">
                  Booked by: {slot.clientName}
                </Typography>
              </>
            }
          />
          <ListItemSecondaryAction>
            {slot.clientId && (
              <Box sx={{ ml: 2 }}>
                <SimpleCallButton
                  targetUserId={slot.clientId}
                  targetUserName={slot.clientName}
                  bookingId={slot.id}
                  bookingType="therapist"
                />
              </Box>
            )}
          </ListItemSecondaryAction>
        </ListItem>
      ))}
    </List>
  );

  return (
    <div style={{ padding: '20px' }}>
      <ToastContainer />
      <h1>Doctor Dashboard</h1>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <Button
          variant={viewMode === 'calendar' ? 'contained' : 'outlined'}
          onClick={() => setViewMode('calendar')}
          sx={{ mr: 2 }}
        >
          Calendar View
        </Button>
        <Button
          variant={viewMode === 'list' ? 'contained' : 'outlined'}
          onClick={() => setViewMode('list')}
        >
          List View
        </Button>
      </Box>

      {viewMode === 'calendar' && (
        <>
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
            <Button onClick={handlePreviousMonth} startIcon={<ArrowBackIosIcon />}>
              Previous Month
            </Button>
            <Typography variant="h6" sx={{ mx: 2 }}>
              {moment(currentDate).format('MMMM YYYY')}
            </Typography>
            <Button onClick={handleNextMonth} endIcon={<ArrowForwardIosIcon />}>
              Next Month
            </Button>
          </Box>

          <Calendar
            localizer={localizer}
            events={[...availableSlots, ...bookedSlots]}
            startAccessor="start"
            endAccessor="end"
            onSelectSlot={handleSelectSlot}
            onSelectEvent={handleSelectEvent}
            selectable
            eventPropGetter={eventStyleGetter}
            style={{ height: 700 }}
            defaultView="month"
            views={['month', 'week', 'day']}
            step={15}
            timeslots={4}
            date={currentDate}
            onNavigate={(date) => setCurrentDate(date)}
          />
        </>
      )}

      {viewMode === 'list' && <SlotListView />}

      <Modal
        open={openModal}
        onClose={() => setOpenModal(false)}
        aria-labelledby="modal-title"
      >
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 400,
          bgcolor: 'background.paper',
          boxShadow: 24,
          p: 4,
        }}>
          <Typography id="modal-title" variant="h6" component="h2" gutterBottom>
            {isEditMode ? 'Edit Slot' : 'Add Slot'}
          </Typography>

          <Typography variant="body2" gutterBottom>
            Date: {selectedDate ? moment(selectedDate).format('MMMM D, YYYY') : ''}
          </Typography>

          <TextField
            fullWidth
            label="Start Time"
            type="time"
            value={startTime}
            onChange={(e) => setStartTime(e.target.value)}
            sx={{ mb: 2 }}
            InputLabelProps={{ shrink: true }}
          />

          <TextField
            fullWidth
            label="End Time"
            type="time"
            value={endTime}
            onChange={(e) => setEndTime(e.target.value)}
            sx={{ mb: 2 }}
            InputLabelProps={{ shrink: true }}
          />

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
            <Button variant="outlined" onClick={() => setOpenModal(false)}>
              Cancel
            </Button>
            <Button variant="contained" onClick={handleAddSlots} disabled={loading}>
              {loading ? 'Processing...' : isEditMode ? 'Update Slot' : 'Add Slot'}
            </Button>
          </Box>
        </Box>
      </Modal>

      <Modal
        open={appointmentDetailsModal}
        onClose={() => setAppointmentDetailsModal(false)}
        aria-labelledby="appointment-details-title"
      >
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 400,
          bgcolor: 'background.paper',
          boxShadow: 24,
          p: 4,
        }}>
          <Typography id="appointment-details-title" variant="h6" component="h2" gutterBottom>
            Appointment Details
          </Typography>

          {selectedAppointment && (
            <>
              <Typography variant="body1" gutterBottom>
                <strong>Client:</strong> {selectedAppointment.clientName}
              </Typography>
              <Typography variant="body1" gutterBottom>
                <strong>Email:</strong> {selectedAppointment.clientEmail}
              </Typography>
              <Typography variant="body1" gutterBottom>
                <strong>Phone:</strong> {selectedAppointment.clientPhone}
              </Typography>
              <Typography variant="body1" gutterBottom>
                <strong>Date:</strong> {moment(selectedAppointment.start).format('MMMM D, YYYY')}
              </Typography>
              <Typography variant="body1" gutterBottom>
                <strong>Time:</strong> {moment(selectedAppointment.start).format('h:mm A')} - {moment(selectedAppointment.end).format('h:mm A')}
              </Typography>

              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                {selectedAppointment.clientId ? (
                  <SimpleCallButton
                    targetUserId={selectedAppointment.clientId}
                    targetUserName={selectedAppointment.clientName}
                    bookingId={selectedAppointment.id}
                    bookingType="therapist"
                  />
                ) : (
                  <Button variant="outlined" disabled sx={{ mt: 2 }}>
                    Client ID not available
                  </Button>
                )}
              </Box>
            </>
          )}
        </Box>
      </Modal>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default DoctorDashboard;